/* Paint, quoted the way the paint finder sells it.
 *
 * The finder (apex_fable5/shopify/sections/afx-paint-finder.liquid) is the source of truth for how a colour becomes a
 * cart line: which Formula tier it needs, tricoat as a 2-can set on 1K, custom fills priced per oz, and the line
 * properties the counter reads off the order (Color, Paint code, Finish, Formula tier, _fx, _hex). This file ports
 * those rules so a quoted line and a website line are the same line. Colour data is the live theme's own files
 * (afx-paint-codes-<c>.json, afx-paint-alias-<c>.json, afx-paint-<make>.json), which the CDN serves with CORS *.
 */

export const PAINT_HANDLE = 'custom-mixed-auto-paint';

export const normCode = (c) => String(c || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

export function shardOf(v) {
  const c = String(v || '').charAt(0).toLowerCase();
  return /[a-z0-9]/.test(c) ? c : '_';
}

/* A row's tag (effect s/m/p, flags t tricoat / k matte, the priced tier 0/1/2, the source = ~ ?). It is the last
   string element from index 3 on when present (nomix-yatu-valspar tools/store/colours.py). */
export function fxOf(row) {
  if (!row) return '';
  for (let i = row.length - 1; i >= 3; i--) {
    const v = row[i];
    if (typeof v === 'string' && /^[smp][tk]*[012]?[=~?]$/.test(v)) return v;
  }
  return '';
}

export function finishOf(name) {
  const n = (name || '').toLowerCase();
  if (/pearl|chromaflair|mica|iridescent/.test(n)) return 'pearl';
  if (/metallic|met\.|firemist|poly/.test(n)) return 'metallic';
  if (/matte|flat/.test(n)) return 'matte';
  return 'solid';
}

export function finishOfFx(fx, name) {
  if (fx) {
    if (fx.indexOf('k') > 0) return 'matte';
    return fx.charAt(0) === 'p' ? 'pearl' : fx.charAt(0) === 'm' ? 'metallic' : 'solid';
  }
  return finishOf(name);
}

export const isTricoat = (name) => /tri-?\s?coat|3[\s-]?stage|three[\s-]?stage/i.test(name || '');
export const isTricoatFx = (fx, name) => (fx ? fx.indexOf('t') > 0 : isTricoat(name));

function hexRgb(h) {
  h = (h || '#888').replace('#', '');
  if (h.length === 3) h = h.replace(/./g, (c) => c + c);
  let n = parseInt(h, 16);
  if (isNaN(n)) n = 0x888888;
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function isRedHex(hex) {
  const [r, g, b] = hexRgb(hex);
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn;
  if (mx < 60 || d < 40 || mx !== r) return false;
  const h = (((g - b) / d) * 60 + 360) % 360;
  return h <= 14 || h >= 346;
}

export const isRed = (name, hex) =>
  /\b(red|rosso|crimson|scarlet|maroon|burgundy|cherry|garnet|ruby)\b/i.test(name || '') || isRedHex(hex);

export const safeHex = (hex) => (/^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(hex || '') ? hex : '#8B8B8B');

export function sizeMeta(label) {
  const l = (label || '').toLowerCase();
  if (/custom|per oz/.test(l)) return { oz: 0, kind: 'custom' };
  if (/aerosol/.test(l)) {
    const m = l.match(/([\d.]+)\s*oz/);
    return { oz: m ? parseFloat(m[1]) : 6.7, kind: 'aerosol' };
  }
  if (/half\s*gallon/.test(l)) return { oz: 64, kind: 'tin' };
  if (/gallon/.test(l)) return { oz: 128, kind: 'tin' };
  if (/quart/.test(l)) return { oz: 32, kind: 'tin' };
  if (/pint/.test(l)) return { oz: 16, kind: 'tin' };
  return { oz: parseInt(l, 10) || 32, kind: 'tin' };
}

export function containerFor(oz) {
  if (oz <= 16) return 'pint can';
  if (oz <= 32) return 'quart can';
  if (oz <= 64) return 'half-gallon can';
  return 'gallon can';
}

/* The paint product as the picker needs it: [{id, system, size, tier, price (number), available, oz, kind}], the
   systems in the product's order, the sizes per system, and the two surcharge tiers by what they cost. */
export function paintCatalog(product) {
  const variants = (product?.variants || []).map((v) => {
    const opt = Object.fromEntries((v.selectedOptions || []).map((o) => [o.name, o.value]));
    const size = opt.Size || 'One size';
    return {
      id: v.id,
      title: v.title,
      sku: v.sku || '',
      system: opt.System || '',
      size,
      tier: opt.Formula || 'Standard',
      price: Number(v.price),
      available: v.availableForSale !== false,
      ...sizeMeta(size),
    };
  });
  const std = variants.filter((v) => v.kind !== 'custom');
  const perOz = variants.filter((v) => v.kind === 'custom');
  const systems = [...new Set(variants.map((v) => v.system))];
  const sizes = [...new Set(std.map((v) => v.size))].sort((a, b) => sizeMeta(a).oz - sizeMeta(b).oz);

  let up = null, top = null;
  const names = [...new Set(variants.map((v) => v.tier).filter((t) => t !== 'Standard'))];
  const ref = std.find((v) => v.tier === 'Standard' && v.available) || std[0];
  const ratio = (t) => {
    const m = std.find((v) => v.tier === t && ref && v.system === ref.system && v.size === ref.size);
    return m && ref && ref.price ? m.price / ref.price : 0;
  };
  for (const t of names) {
    const r = ratio(t);
    if (/pearl|effect/i.test(t) || (r > 1.1 && r < 1.175)) { if (!up) up = t; }
    else if (/red|premium/i.test(t) || r >= 1.175) { if (!top) top = t; }
  }
  return { productId: product?.id, title: product?.title, variants, std, perOz, systems, sizes, tierUp: up, tierTop: top };
}

const fxTier = (fx) => {
  const m = /([012])[=~?]$/.exec(fx || '');
  return m ? m[1] : '';
};

/* Which Formula tier(s) a colour needs: tricoat → a 2-can set (base + the +15% pearl mid-coat). */
export function colorTiers(c, cat) {
  c = c || {};
  const tri = isTricoatFx(c.fx, c.name);
  const t = fxTier(c.fx);
  let base = 'Standard';
  if (c.tierOverride) base = c.tierOverride;
  else if (t === '2' && cat.tierTop) base = cat.tierTop;
  else if (t === '1' && cat.tierUp) base = cat.tierUp;
  else if (t === '0') base = 'Standard';
  else if (isRed(c.name, c.hex) && cat.tierTop) base = cat.tierTop;
  else if (!tri && finishOfFx(c.fx, c.name) === 'pearl' && cat.tierUp) base = cat.tierUp;
  return { tri, base, pearl: tri && cat.tierUp ? cat.tierUp : tri ? base : null, costed: t !== '' && !c.tierOverride };
}

export function variantFor(cat, system, size, tier) {
  const s = cat.std;
  return (
    s.find((v) => v.system === system && v.size === size && v.tier === (tier || 'Standard')) ||
    s.find((v) => v.system === system && v.size === size && v.tier === 'Standard') ||
    s.find((v) => v.system === system && v.size === size) ||
    null
  );
}

export function perOzFor(cat, system, tier) {
  const s = cat.perOz;
  return (
    s.find((v) => v.system === system && v.tier === (tier || 'Standard') && v.available) ||
    s.find((v) => v.system === system && v.tier === 'Standard' && v.available) ||
    s.find((v) => v.system === system && v.available) ||
    null
  );
}

/* Tricoat can only be sprayed as a basecoat: the 3-stage finish locks the system to 1K. */
export function systemFor(cat, wanted, tri) {
  if (tri) return cat.systems.find((s) => /^1K/i.test(s)) || wanted || cat.systems[0];
  return wanted && cat.systems.includes(wanted) ? wanted : cat.systems[0];
}

const cap = (s) => s.replace(/^./, (x) => x.toUpperCase());

/* A colour + choices → the quote lines, exactly as the finder would put them in the cart.
   color: {name, code, hex, fx, make?, tierOverride?}
   choice: {system, size, qty, custom (bool), oz, vehicle} */
export function paintLines(color, choice, cat) {
  const T = colorTiers(color, cat);
  const system = systemFor(cat, choice.system, T.tri);
  const custom = !!choice.custom;
  const qty = Math.max(1, Math.round(Number(custom ? choice.oz : choice.qty) || 1));
  const pick = (tier) => (custom ? perOzFor(cat, system, tier) : variantFor(cat, system, choice.size, tier));
  const vB = pick(T.base);
  if (!vB) return { lines: [], error: `No ${custom ? 'per-oz' : choice.size} variant for ${system}` };
  const vP = T.tri ? pick(T.pearl || T.base) || vB : null;

  const props = [
    ['Color', color.name || ''],
    ['Paint code', color.code || ''],
  ];
  if (choice.vehicle) props.push(['Vehicle', choice.vehicle]);
  props.push(['Finish', T.tri ? 'Tricoat (3-stage)' : cap(finishOfFx(color.fx, color.name))]);
  if (color.fx) props.push(['_fx', color.fx]);
  props.push(['Formula tier', T.base + (T.costed ? ' (priced from the formula)' : '')]);
  props.push(['_hex', color.hex || '']);
  if (custom) {
    props.push(['Custom fill', `${qty} oz`]);
    props.push(['Packaged in', containerFor(qty)]);
  }

  const line = (v, extra) => ({
    kind: 'paint',
    variantId: v.id,
    title: 'Custom-Mixed Auto Paint',
    variantTitle: v.title,
    sku: v.sku,
    quantity: qty,
    listPrice: v.price,
    unitPrice: v.price,
    hex: safeHex(color.hex),
    attributes: [...props, ...extra].map(([key, value]) => ({ key, value: String(value) })),
  });

  const lines = T.tri
    ? [line(vB, [['Component', 'Stage 1 of 2 — color basecoat']]), line(vP, [['Component', 'Stage 2 of 2 — pearl mid-coat']])]
    : [line(vB, [])];
  return { lines, tiers: T, system };
}

/* ── how a line reads (in the app and in the email) ──
   A paint line is named by its colour and what kind of paint it is; the long product title stays on the order.
   `_quote_title` (hidden at checkout) is a name typed in the app; `Note` is a visible line note for the customer. */
export const attrsOf = (l) => Object.fromEntries((l.attributes || []).map((x) => [x.key, x.value]));
export const lineSystem = (l) => (l.variantTitle || '').split(' / ')[0] || '';
export function systemKind(system) {
  if (/^2K/i.test(system || '')) return 'single';
  if (/^1K/i.test(system || '')) return 'base';
  return '';
}
export const SYSTEM_LABEL = { single: 'Single stage', base: 'Basecoat' };
export const SYSTEM_NOTE = { single: '2K urethane — no clear needed', base: 'clear coat goes over it' };

export function displayTitle(l) {
  const a = attrsOf(l);
  if (a._quote_title) return a._quote_title;
  if (l.kind !== 'paint') return l.title;
  const name = `${a.Color || 'Custom color'}${a['Paint code'] ? ` (${a['Paint code']})` : ''}`;
  const kind = a.Component ? 'base' : systemKind(lineSystem(l));
  return kind ? `${name} — ${SYSTEM_LABEL[kind]}${a.Component ? ` ${a.Component.replace(/ — .*$/, '').replace(/^Stage /, 'stage ')}` : ''}` : name;
}

export function paintSummary(line) {
  const a = attrsOf(line);
  const [system, size, tier] = (line.variantTitle || '').split(' / ');
  const kind = systemKind(system);
  const parts = [];
  if (a['Custom fill']) parts.push(`${a['Custom fill']} in a ${a['Packaged in']}`);
  else if (size) parts.push(size);
  if (a.Component) parts.push(a.Component.replace(/^Stage \d of 2 — /, ''));
  else if (kind) parts.push(`${system} (${SYSTEM_NOTE[kind]})`);
  if (tier && tier !== 'Standard') parts.push(`${tier} formula`);
  if (a.Vehicle) parts.push(a.Vehicle);
  if (a._quote_title && (a.Color || a['Paint code'])) parts.push(`${a.Color || ''}${a['Paint code'] ? ` (${a['Paint code']})` : ''}`.trim());
  if (a.Note) parts.push(a.Note);
  return parts.join(' · ');
}

export function displayDetail(l) {
  if (l.kind === 'paint') return paintSummary(l);
  const a = attrsOf(l);
  if (a._unit === 'oz') {
    return [`${l.quantity} oz`, a['For paint'] && `for ${a['For paint']}`, a['Mix ratio'] && `mixes ${a['Mix ratio']}`, a.Note].filter(Boolean).join(' · ');
  }
  return [a._quote_title ? l.title : '', l.variantTitle, l.sku && `SKU ${l.sku}`, a.Role && a['For paint'] ? `${a.Role} for ${a['For paint']}` : '', a.Note]
    .filter(Boolean)
    .join(' · ');
}

/* A paint code as typed, without the stray punctuation round it ("7511)" → "7511"); inside it stays as written
   (NH-731P, 61/WA638R). */
export const cleanCode = (c) => String(c ?? '').trim().replace(/^[^A-Za-z0-9]+|[^A-Za-z0-9]+$/g, '');

/* ── the paint on the order itself ──
   Shopify's order page and its printed packing slip show the order note, while line properties are easy to miss
   there and the default packing slip leaves them out. So a quote with paint writes what to mix — every paint code,
   colour, system, size, finish and what goes with it — at the top of the note, between these two marker lines. It
   is rebuilt from the lines on every save and taken off again when the quote is read back, so staff only ever see
   and edit their own note. */
export const PAINT_NOTE_HEAD = '*** PAINT TO MIX ***';
export const PAINT_NOTE_TAIL = '*** end of paint ***';

export function paintNote(lines) {
  const paints = (lines || []).filter((l) => l.kind === 'paint' || 'Paint code' in attrsOf(l));
  if (!paints.length) return '';
  const out = [PAINT_NOTE_HEAD];
  for (const l of paints) {
    const a = attrsOf(l);
    out.push(`PAINT CODE ${cleanCode(a['Paint code']) || '(none given)'}${a.Color ? ` · ${a.Color}` : ''}${a.Finish ? ` · ${a.Finish}` : ''}`);
    const amount = a['Custom fill'] ? `${a['Custom fill']}${a['Packaged in'] ? ` in a ${a['Packaged in']}` : ''} ·` : `${l.quantity} ×`;
    const what = [`${amount} ${l.variantTitle || l.title || ''}`.trim(), a.Component, a.Vehicle && `for ${a.Vehicle}`, a.Note].filter(Boolean);
    out.push(`   ${what.join(' · ')}`);
  }
  for (const l of lines) {
    const a = attrsOf(l);
    if (!a['For paint'] || paints.includes(l)) continue;
    out.push(`   + ${a.Role ? `${a.Role}: ` : ''}${l.title}${a.Amount ? ` — ${a.Amount}` : ` × ${l.quantity}`}${a['Mix ratio'] ? ` (${a['Mix ratio']})` : ''} · for ${a['For paint']}`);
  }
  out.push(PAINT_NOTE_TAIL);
  return out.join('\n');
}

/* The note without the paint block (a block whose end marker was deleted by hand ends at its first blank line). */
export function stripPaintNote(note) {
  let s = String(note || '');
  for (let i = s.indexOf(PAINT_NOTE_HEAD); i >= 0; i = s.indexOf(PAINT_NOTE_HEAD)) {
    const j = s.indexOf(PAINT_NOTE_TAIL, i);
    const gap = s.indexOf('\n\n', i);
    const end = j >= 0 ? j + PAINT_NOTE_TAIL.length : gap >= 0 ? gap : s.length;
    s = s.slice(0, i) + s.slice(end);
  }
  return s.trim();
}

/* The note a quote is saved with: the paint block first, then the staff's own note. */
export function withPaintNote(note, lines) {
  const own = stripPaintNote(note);
  const block = paintNote(lines);
  return [block, own].filter(Boolean).join('\n\n');
}

/* Change one attribute (empty removes it); the rest keep their order. */
export function setAttr(attributes, key, value) {
  const v = String(value ?? '').trim();
  const list = (attributes || []).filter((x) => x.key !== key || v);
  const i = list.findIndex((x) => x.key === key);
  if (!v) return list;
  if (i >= 0) return list.map((x) => (x.key === key ? { key, value: v } : x));
  return [...list, { key, value: v }];
}

/* ── the live colour files ── */

const memo = new Map();
function getJSON(url, optional) {
  if (!memo.has(url)) {
    memo.set(
      url,
      fetch(url).then((r) => {
        if (r.ok) return r.json();
        if (optional) return {};
        throw new Error(`${r.status} ${url}`);
      }).catch((e) => {
        memo.delete(url);
        if (optional) return {};
        throw e;
      }),
    );
  }
  return memo.get(url);
}

/* Colours under a code, every make: [{key, code, name, hex, make, fx}], best match first. `base` is the theme's asset
   folder (…/cdn/shop/t/<n>/assets/). A code the store prints several ways ("61/WA638R/GBD") is found by any of them. */
export async function searchCode(base, query, { make = '', limit = 40 } = {}) {
  const q = normCode(query);
  if (q.length < 2) return [];
  const sh = shardOf(q);
  const [codes, alias] = await Promise.all([getJSON(`${base}afx-paint-codes-${sh}.json`), getJSON(`${base}afx-paint-alias-${sh}.json`, true)]);
  const keys = new Map(); // key → rank (0 exact, 1 prefix)
  for (const k of Object.keys(codes)) {
    const n = normCode(k.split('/')[0]);
    if (n === q) keys.set(k, 0);
    else if (n.startsWith(q) && !keys.has(k)) keys.set(k, 1);
  }
  const more = [];
  for (const [a, ks] of Object.entries(alias)) {
    const n = normCode(a);
    if (n === q || n.startsWith(q)) for (const k of ks) if (!keys.has(k)) { keys.set(k, n === q ? 0 : 1); more.push(k); }
  }
  const shards = {};
  await Promise.all(
    [...new Set(more.map(shardOf))].filter((s) => s !== sh).map(async (s) => { shards[s] = await getJSON(`${base}afx-paint-codes-${s}.json`).catch(() => ({})); }),
  );
  const out = [];
  const m = make.toLowerCase();
  for (const [k, rank] of keys) {
    const src = shardOf(k) === sh ? codes : shards[shardOf(k)] || {};
    for (const e of src[k] || []) {
      const row = [k, ...e];
      const item = { key: k, code: k.split('/')[0], codes: k, name: e[0], hex: e[1], make: e[2] || '', fx: fxOf(row), rank };
      if (m && item.make.toLowerCase() !== m) continue;
      out.push(item);
    }
  }
  out.sort((a, b) => a.rank - b.rank || a.code.length - b.code.length || a.make.localeCompare(b.make) || a.name.localeCompare(b.name));
  return out.slice(0, limit);
}

export async function makes(base) {
  const idx = await getJSON(`${base}afx-paint-index.json`);
  return idx.makes || [];
}

/* A make's colours, newest year first, one row per (code, name): [{code, name, hex, fx, models: "4Runner 2025, …"}]. */
export async function makeColours(base, slug) {
  const d = await getJSON(`${base}afx-paint-${slug}.json`);
  const by = new Map();
  for (const [model, years] of Object.entries(d.models || {})) {
    for (const [year, rows] of Object.entries(years)) {
      for (const r of rows) {
        const id = `${r[0]}|${r[1]}`;
        let c = by.get(id);
        if (!c) by.set(id, (c = { key: r[0], code: r[0], name: r[1], hex: r[2], make: d.make, fx: fxOf(r), uses: [], last: 0 }));
        c.uses.push(`${year} ${model}`);
        c.last = Math.max(c.last, parseInt(year, 10) || 0);
      }
    }
  }
  return [...by.values()].sort((a, b) => b.last - a.last || a.name.localeCompare(b.name));
}

/* ── complete the job: the finder's add-ons, sized by mix ratio ──
   The finder's rules (afx-paint-finder.liquid addonPlan): 1K aerosol → one clear aerosol (2K is the pro pick, required
   for a tricoat); 1K tins → reducer (ticked: basecoat will not spray unreduced) and a size-matched clear when the store
   has one; 2K aerosol → nothing (internal hardener); 2K tins → the activator (ticked: it will not cure without it).
   The quote sizes the activator and reducer by volume instead of one per can: 2K Direct Gloss (Valspar SP150) mixes
   4:1 with SPA150, so a gallon takes a quart. */

/* A container's size from its SKU or title: VAL.SPA150.QT → 32 oz, HIT.W7870-1.GL → 128 oz. Null for a spray can. */
export function containerOz(sku, title) {
  const s = `${sku || ''} ${title || ''}`;
  if (/\b5\s*-?\s*(gal|gallon)\b|\.5G\b/i.test(s)) return 640;
  if (/\.QT\b|\bquart\b|\bqt\b/i.test(s)) return 32;
  if (/\.GL\b|\bgallon\b|\bgal\b/i.test(s)) return 128;
  if (/\.PT\b|\bpint\b/i.test(s)) return 16;
  return null;
}

/* Ounces of paint in a set of lines (custom fills count their ounces, cans their size × quantity). */
export function paintOunces(lines, cat) {
  let oz = 0;
  for (const l of lines) {
    const v = cat.variants.find((x) => x.id === l.variantId);
    if (!v) continue;
    oz += v.kind === 'custom' ? l.quantity : (v.oz || 0) * l.quantity;
  }
  return oz;
}

/* The add-ons that fit this paint, each with its quantity and whether it starts ticked.
   addons: { key: {variantId, title, sku, price, oz, image} } (the finder's, checked against the live product). */
/* Interchangeable products for one job: the first present is the default unless Settings names another. */
export const ADDON_CHOICES = { activator: ['act', 'act_ht'] };

export function addonPlan({ lines, tiers, system }, cat, addons, ratios = {}, chosen = {}) {
  if (!lines.length) return [];
  const first = cat.variants.find((x) => x.id === lines[0].variantId);
  const isAero = first?.kind === 'aerosol';
  const oneK = tiers.tri || /^1K/i.test(system || '');
  const oz = paintOunces(lines, cat);
  const cans = lines.reduce((n, l) => n + l.quantity, 0);
  const out = [];
  const add = (key, group, role, on, qty, why) => {
    const a = addons[key];
    if (a) out.push({ key, group, role, on, qty: Math.max(1, qty), why, ...a });
  };
  const byVolume = (key, ratio) => {
    const a = addons[key];
    if (!a?.oz || !(ratio > 0)) return { qty: lines[0].quantity, why: '' };
    const need = oz / ratio;
    return { qty: Math.ceil(need / a.oz - 1e-9), why: `${ratio}:1 → ${Math.round(need * 10) / 10} oz` };
  };
  if (oneK) {
    if (isAero) {
      add('c2k', 'clear', 'Clearcoat', tiers.tri, cans, tiers.tri ? 'a tricoat needs clear' : 'pro pick');
      add('c1k', 'clear', 'Clearcoat', false, cans, 'easy touch-up');
    } else {
      const small = oz <= 32;
      const ck = small ? 'cqt' : 'cgal';
      add(ck, 'clear', 'Clearcoat', !!tiers.tri, 1, 'activator sold separately');
      const rk = small && addons.rqt ? 'rqt' : 'rgal';
      const r = byVolume(rk, Number(ratios.reducer) || 1);
      add(rk, 'reducer', 'Reducer', true, r.qty, r.why || 'needed to spray');
    }
  } else if (!isAero) {
    const keys = ADDON_CHOICES.activator.filter((k) => addons[k]);
    const key = [chosen.activator, ratios.activatorProduct, ...keys].find((k) => k && addons[k]);
    if (key) {
      /* poured at the shop from the quart: the customer gets the ounces the mix needs, priced by the ounce */
      const ratio = Number(ratios.activator) || 4;
      const need = Math.max(1, Math.ceil(oz / ratio - 1e-9));
      add(key, 'activator', 'Activator', true, need, `${ratio}:1 → ${need} oz`);
      const it = out[out.length - 1];
      it.unit = 'oz';
      it.ratio = ratio;
      it.price = perOz(addons[key]);
      it.choices = keys.map((k) => ({ key: k, title: addons[k].title, price: perOz(addons[k]), unit: 'oz' }));
    }
  }
  return out;
}

/* An add-on as a quote line, with the properties the finder gives it. */
/* A container's price by the ounce (a quart of SPA150 at $129.10 → $4.03/oz). */
export const perOz = (a) => (a?.oz ? Math.round((a.price / a.oz) * 100) / 100 : a?.price || 0);

const shortName = (t) => String(t || '').replace(/\s*[—-]\s*\d.*$/, '').trim();

export function addonLine(a, color) {
  const forPaint = `${color.name || ''}${color.code ? ` (${color.code})` : ''}`;
  if (a.unit === 'oz') {
    /* a custom line, not the product: the quart stays on the shelf and the customer pays for what is poured */
    return {
      kind: 'custom',
      title: `${shortName(a.title)} — poured to order (per oz)`,
      variantTitle: '',
      sku: '',
      quantity: a.qty,
      listPrice: a.price,
      unitPrice: a.price,
      taxable: true,
      requiresShipping: true,
      attributes: [
        { key: 'For paint', value: forPaint },
        { key: 'Role', value: a.role },
        { key: 'Amount', value: `${a.qty} oz` },
        ...(a.ratio ? [{ key: 'Mix ratio', value: `${a.ratio}:1` }] : []),
        { key: '_unit', value: 'oz' },
      ],
    };
  }
  return {
    kind: 'product',
    variantId: a.variantId,
    title: a.title,
    variantTitle: '',
    sku: a.sku || '',
    quantity: a.qty,
    listPrice: a.price,
    unitPrice: a.price,
    image: a.image || '',
    attributes: [
      { key: 'For paint', value: `${color.name || ''}${color.code ? ` (${color.code})` : ''}` },
      { key: 'Role', value: a.role },
    ],
  };
}
